import React from 'react';
import { useLayout } from '../../context/LayoutContext';
import { Icon } from '../common/Icon';
import { ChevronRight, Globe } from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
  const { categories, getModulesByPlacement, activeModuleId, setActiveCategory, setActiveModule } = useLayout();

  // Csak a bal oldalsávhoz (sidebar) rendelt modulok lekérése (szerepkör és appMode szerint szűrve)
  const sidebarModules = getModulesByPlacement('sidebar');

  // Azon kategóriák, amelyekhez tartozik megjelenítendő modul a bal menüben
  const sidebarCategories = categories.filter((cat) =>
    sidebarModules.some((m) => m.category === cat.id)
  );

  return (
    <aside
      className={`fixed lg:static top-[57px] lg:top-auto left-0 z-30 h-[calc(100vh-57px)] lg:h-full bg-white dark:bg-[#0b0f1a] border-r border-slate-200 dark:border-slate-800 transition-all duration-300 overflow-y-auto flex flex-col justify-between shrink-0 ${
        isOpen ? 'w-64' : 'w-0 lg:w-20 -translate-x-full lg:translate-x-0'
      }`}
    >
      <div className="p-3 space-y-6">
        {sidebarCategories.map((cat) => {
          const catModules = sidebarModules.filter((m) => m.category === cat.id);
          if (catModules.length === 0) return null;

          return (
            <div key={cat.id} className="space-y-1">
              <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2">
                <Icon name={cat.icon} className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" />
                <span className={isOpen ? 'block' : 'lg:hidden'}>{cat.title}</span>
              </div>

              <div className="space-y-0.5">
                {catModules.map((mod) => {
                  const isActive = activeModuleId === mod.id;

                  return (
                    <button
                      key={mod.id}
                      onClick={() => {
                        setActiveCategory(cat.id);
                        setActiveModule(mod.id);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition cursor-pointer ${
                        isActive
                          ? 'bg-indigo-50 border border-indigo-200 text-indigo-700 dark:bg-blue-600/20 dark:text-cyan-300 dark:border-blue-500/30 shadow-xs'
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/60'
                      }`}
                      title={mod.description}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Icon
                          name={mod.icon}
                          className={`w-4 h-4 shrink-0 ${isActive ? 'text-indigo-600 dark:text-cyan-400' : 'text-slate-400'}`}
                        />
                        <span className={`truncate ${isOpen ? 'block' : 'lg:hidden'}`}>{mod.title}</span>
                      </div>
                      {isActive && <ChevronRight className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Alsó tájékoztató doboz és Publikus Portál gomb */}
      <div className="p-3 border-t border-slate-200 dark:border-slate-800/80 space-y-2">
        <button
          onClick={() => setActiveModule('public_portal')}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold text-indigo-700 dark:text-cyan-300 bg-indigo-50/80 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 border border-indigo-200/80 dark:border-indigo-500/30 transition cursor-pointer ${
            activeModuleId === 'public_portal' ? 'ring-2 ring-indigo-500' : ''
          }`}
          title="Közzétételi oldal megtekintése látogatóként"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <Globe className="w-4 h-4 shrink-0 text-indigo-600 dark:text-cyan-400" />
            <span className={isOpen ? 'block' : 'lg:hidden'}>Publikus Portál</span>
          </div>
          <ChevronRight className={`w-3.5 h-3.5 opacity-60 shrink-0 ${isOpen ? 'block' : 'lg:hidden'}`} />
        </button>

        <div className={`p-3 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 ${isOpen ? 'block' : 'lg:hidden'}`}>
          <div className="font-semibold text-slate-700 dark:text-slate-300 mb-0.5">CXII/2011 törvény</div>
          <div>1. sz. melléklet szerinti adatszolgáltatási megfelelőség</div>
        </div>
      </div>
    </aside>

  );
};
